import { useCallback, useEffect, useRef, useState } from "react"

import { useMediaQuery } from "@mui/material"
import { GOOGLE_CLIENT_ID } from "api/api.config"
import { setGoogleToken } from "store/auth/auth.slice"
import { size } from "utils/constants/size"

import { useAppDispatch } from "../store"

interface IProps {
  mutate: ({ token }: { token: string }) => void
  isDispatch?: boolean
}

export const useGoogle = ({ mutate, isDispatch = false }: IProps) => {
  const google = window.google
  const [token, setToken] = useState<string | null>(null)
  const [loadedGoogle, setLoadedGoogle] = useState(false)
  const dispatch = useAppDispatch()
  const isTablet = useMediaQuery(size.MIN_WIDTH_410)
  const isMobile = useMediaQuery(size.MAX_WIDTH_375)

  // A callback ref (rather than useRef) so renderButton also re-runs when the
  // target div remounts under the same ref identity (e.g. after the
  // Clever-restricted takeover unmounts and remounts the form).
  const [buttonNode, setButtonNode] = useState<HTMLDivElement | null>(null)
  const refButton = useCallback((node: HTMLDivElement | null) => {
    setButtonNode(node)
  }, [])

  const clientId = GOOGLE_CLIENT_ID

  const handleGoogleRegister = useCallback(
    async (res: { credential: string }) => {
      mutate({ token: res.credential })
      setToken(res.credential)
      if (isDispatch) dispatch(setGoogleToken(res.credential))
    },
    [mutate, isDispatch, dispatch],
  )

  const handleGoogleRegisterRef = useRef(handleGoogleRegister)
  handleGoogleRegisterRef.current = handleGoogleRegister

  useEffect(() => {
    const check = setInterval(() => {
      if (window.google) {
        setLoadedGoogle(true)
        clearInterval(check)
      }
    }, 100)

    return () => clearInterval(check)
  }, [])

  useEffect(() => {
    if (!google || !loadedGoogle) return

    google.accounts.id.initialize({
      client_id: clientId,
      callback: (res: { credential: string }) =>
        handleGoogleRegisterRef.current(res),
    })
  }, [google, loadedGoogle, clientId])

  useEffect(() => {
    if (!google || !loadedGoogle || !buttonNode) return

    buttonNode.innerHTML = ""
    google.accounts.id.renderButton(buttonNode, {
      width: isTablet ? 400 : !isMobile ? 360 : 330,
    })
  }, [google, loadedGoogle, isTablet, isMobile, buttonNode])

  return {
    token,
    refButton,
  }
}
